import { useTranslation } from 'react-i18next';
import type { Finding } from '@complan/contracts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { resolveFindingMessage } from '@/shared/i18n';
import { toResolvableFinding } from './admin-findings';

/**
 * The findings a rejected admin write surfaces (task 23.6).
 *
 * A write the server blocked returns a `validation_failed` carrying `findings`
 * (R16.6, R16.10, R16.11, R16.12, R8.9). This component renders them: the
 * server decided, the client only surfaces. Each finding's text resolves through
 * the i18next catalogue by its `messageKey`, with the finding's own `detail`
 * supplied as interpolation variables and an administrator override winning when
 * present. Nothing here re-derives a rule verdict.
 */
export function FindingsAlert({
  findings,
}: {
  readonly findings: readonly Finding[];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (findings.length === 0) {
    return null;
  }
  return (
    <Alert variant="destructive" className="border-[#F0C3BC] bg-[#FCEDEB]">
      <AlertTitle className="text-[#8E2A1E]">{t('admin.findings.title')}</AlertTitle>
      <AlertDescription>
        <ul className="list-none">
          {findings.map((finding, index) => (
            <li
              key={`${finding.ruleId}-${String(index)}`}
              className="text-[11.5px] text-[#8E2A1E]"
            >
              {resolveFindingMessage(t, toResolvableFinding(finding))}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
